/**
 * Table Utilities
 *
 * Shared utility functions for order tables and data display
 * Extracted from OrdersTable and ResizableOrdersTable to avoid duplication
 */

import type { ParsedProduct } from "@/types/order";

// ============================================================================
// Parsed Items Formatting
// ============================================================================

export interface FormatParsedItemsOptions {
  /** Maximum number of items to show before truncating */
  maxItems?: number;
  /** Whether to show quantity in format "name x quantity" */
  showQuantity?: boolean;
  /** Maximum character length before truncating raw text */
  maxLength?: number;
  /** Fallback text when items is null/empty */
  fallbackText?: string;
  /** Show "+N" suffix instead of "..." when truncating (e.g. "Honey +2") */
  plusN?: boolean;
}

/**
 * Format parsed order items for display in table cells
 *
 * Handles two common use cases:
 * 1. Brief preview: Show first 2 items only (OrdersTable)
 * 2. Full list: Show all items with quantities (ResizableOrdersTable)
 *
 * @param items - JSON string of parsed items or raw text
 * @param options - Formatting options
 * @returns Formatted string for display
 *
 * @example
 * // Brief preview (first 2 items)
 * formatParsedItemsPreview(order.parsed_items, { maxItems: 2 })
 * // Output: "Soap, Shampoo..."
 *
 * @example
 * // Full list with quantities
 * formatParsedItemsPreview(order.parsed_items, { showQuantity: true })
 * // Output: "Soap x2, Shampoo x1, Towel x3"
 */
export const formatParsedItemsPreview = (
  items: string | null,
  options: FormatParsedItemsOptions = {}
): string => {
  const {
    maxItems = undefined,
    showQuantity = false,
    maxLength = 100,
    fallbackText = "",
    plusN = false,
  } = options;

  if (!items) return fallbackText;

  try {
    const parsed = JSON.parse(items);

    if (Array.isArray(parsed) && parsed.length > 0) {
      // Take subset if maxItems specified
      const itemsToShow = maxItems ? parsed.slice(0, maxItems) : parsed;

      // Format each item
      const formatted = itemsToShow.map((item: ParsedProduct) => {
        const name = item.name || "";
        if (showQuantity && item.quantity) {
          return `${name} x${item.quantity}`;
        }
        return name;
      }).filter(Boolean);

      // Join and add ellipsis or +N if truncated
      const result = formatted.join(", ");
      const wasTruncated = maxItems && parsed.length > maxItems;
      if (wasTruncated) {
        const remaining = parsed.length - maxItems;
        return plusN ? `${result} +${remaining}` : result + "...";
      }
      return result;
    }
  } catch (e) {
    // If not valid JSON, return truncated raw text
    return items.length > maxLength
      ? items.substring(0, maxLength) + "..."
      : items;
  }

  return fallbackText;
};

// ============================================================================
// Column Selection
// ============================================================================

/**
 * Standard order columns for Supabase queries
 * Use this to ensure consistent column selection across queries
 *
 * @example
 * supabase.from("orders").select(getOrderColumns())
 */
export const getOrderColumns = (): string => {
  return `
    id,
    user_id,
    customer_id,
    order_number,
    customer_name,
    customer_phone,
    message_raw,
    parsed_items,
    address,
    total_price,
    order_status,
    source,
    category,
    assigned_to,
    comments,
    csr_name,
    is_archived,
    created_at,
    updated_at
  `;
};

/**
 * Minimal order columns for list views (lighter queries)
 * Use when you don't need all order details
 *
 * @example
 * supabase.from("orders").select(getOrderColumnsMinimal())
 */
export const getOrderColumnsMinimal = (): string => {
  return `
    id,
    order_number,
    customer_name,
    customer_phone,
    parsed_items,
    total_price,
    order_status,
    source,
    category,
    created_at,
    is_archived
  `;
};

// ============================================================================
// Price Formatting
// ============================================================================

/**
 * Format price with currency symbol
 *
 * @param price - Price value (can be null)
 * @param currency - Currency symbol (default: ₾ - Georgian Lari)
 * @returns Formatted price string
 *
 * @example
 * formatPrice(123.45) // "₾123.45"
 * formatPrice(null) // "-"
 */
export const formatPrice = (
  price: number | null | undefined,
  currency: string = "₾"
): string => {
  if (price === null || price === undefined) return "-";
  return `${currency}${price.toFixed(2)}`;
};

// ============================================================================
// Date Formatting
// ============================================================================

/**
 * Format date for table display
 *
 * @param dateString - ISO date string
 * @param includeTime - Whether to include time (default: false)
 * @returns Formatted date string
 *
 * @example
 * formatTableDate("2025-11-23T10:30:00") // "Nov 23, 2025"
 * formatTableDate("2025-11-23T10:30:00", true) // "Nov 23, 2025 10:30"
 */
export const formatTableDate = (
  dateString: string | null | undefined,
  includeTime: boolean = false
): string => {
  if (!dateString) return "-";

  try {
    const date = new Date(dateString);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();

    if (includeTime) {
      const hh = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      return `${dd}.${mm}.${yyyy} ${hh}:${min}`;
    }

    return `${dd}.${mm}.${yyyy}`;
  } catch (e) {
    return dateString;
  }
};

// ============================================================================
// Text Truncation
// ============================================================================

/**
 * Truncate text to specified length with ellipsis
 *
 * @param text - Text to truncate
 * @param maxLength - Maximum length (default: 50)
 * @returns Truncated text with ellipsis if needed
 *
 * @example
 * truncateText("This is a very long text...", 10) // "This is a..."
 */
export const truncateText = (
  text: string | null | undefined,
  maxLength: number = 50
): string => {
  if (!text) return "";
  return text.length > maxLength
    ? text.substring(0, maxLength) + "..."
    : text;
};
